import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import './ScrunchExplainer.css';

const SESSION_KEY = 'scrunch-demo-unlocked';

function isUnlocked() {
  try { return window.sessionStorage.getItem(SESSION_KEY) === 'true'; } catch { return false; }
}

const steps = [
  { number: '01', title: 'Send a public URL', copy: 'The React page sends an encoded URL, optional crawler identity, and optional AI provider choice to FastAPI.', label: 'Browser → API', tone: 'violet' },
  { number: '02', title: 'Validate before connecting', copy: 'The API resolves the hostname, blocks non-public addresses, checks robots.txt, and repeats those checks for every redirect.', label: 'Safety gate', tone: 'amber' },
  { number: '03', title: 'Fetch within clear limits', copy: 'By default, each host gets ten outbound attempts per minute. Requests use separate timeouts, up to three attempts, a 2 MiB body cap, and at most ten redirects.', label: 'Controlled fetch', tone: 'blue' },
  { number: '04', title: 'Create readable content', copy: 'The service preserves the source, extracts title and description, and converts HTML to Markdown. Optional Foundry or OpenAI extraction receives up to 20,000 Markdown characters.', label: 'Metadata + Markdown + AI', tone: 'violet' },
  { number: '05', title: 'Return one typed response', copy: 'The UI receives JSON containing the final URL, raw source, Markdown, metadata, timing, and any requested AI insights. Downloaded HTML is displayed as text, never executed.', label: 'Structured output', tone: 'green' },
];

const errors = [
  ['Invalid or unsafe URL', '403 / 422', 'Bad URL, credentials, or a destination resolving to a non-public address'],
  ['Crawler not permitted', '403 / 503', 'robots.txt denies the effective User-Agent or cannot be retrieved safely'],
  ['Destination refuses', '401 / 403 / 404', 'Authentication, access denial, browser challenge, or missing page'],
  ['Request exceeds a limit', '413 / 429', 'Decoded body exceeds 2 MiB or the host reaches its rolling request limit'],
  ['Upstream cannot finish', '502 / 504', 'DNS, connection, redirect, exhausted retry, or timeout failure'],
];

export default function ScrunchExplainer() {
  if (!isUnlocked()) return <Navigate to="/scrunch-demo" replace />;

  return (
    <main className="scrunch-explain-page">
      <header className="scrunch-explain-header">
        <Link to="/scrunch-demo" className="scrunch-explain-brand"><span>S</span><b>SCRUNCH</b></Link>
        <Link to="/scrunch-demo" className="scrunch-explain-back">
          <span aria-hidden="true">←</span> Back<span className="scrunch-explain-back-detail"> to retriever</span>
        </Link>
      </header>

      <article className="scrunch-explain-shell">
        <header className="scrunch-explain-hero">
          <p>WEB CONTENT RETRIEVER · SYSTEM EXPLAINER</p>
          <h1>One URL in.<br />Useful content out.</h1>
          <span>A quick look at how the retriever fetches public pages safely and optionally adds AI-powered insights.</span>
        </header>

        <section className="scrunch-plain-summary" aria-label="System summary">
          <div><small>INPUT</small><strong>A public URL</strong><span>plus an optional crawler identity</span></div>
          <b aria-hidden="true">→</b>
          <div><small>PROCESS</small><strong>Validate, fetch, convert</strong><span>then optionally extract with AI</span></div>
          <b aria-hidden="true">→</b>
          <div><small>OUTPUT</small><strong>Structured JSON</strong><span>plus readable demo panels</span></div>
        </section>

        <section className="scrunch-explain-section">
          <div className="scrunch-explain-heading"><p>THE WHOLE PIPELINE</p><h2>Five steps, start to finish</h2></div>
          <div className="scrunch-step-list">
            {steps.map((step, index) => (
              <article className={`scrunch-step scrunch-step-${step.tone}`} key={step.number}>
                <div className="scrunch-step-number">{step.number}</div>
                <div className="scrunch-step-copy"><span>{step.label}</span><h3>{step.title}</h3><p>{step.copy}</p></div>
                {index < steps.length - 1 && <i aria-hidden="true" />}
              </article>
            ))}
          </div>
        </section>

        <section className="scrunch-explain-section">
          <div className="scrunch-explain-heading"><p>WHEN A REQUEST STOPS</p><h2>Clear errors, no guesswork</h2></div>
          <div className="scrunch-error-table" role="table" aria-label="Retriever errors">
            {errors.map(([name, status, explanation]) => <div role="row" key={name}><strong role="cell">{name}</strong><code role="cell">{status}</code><span role="cell">{explanation}</span></div>)}
          </div>
          <aside className="scrunch-cloudflare-note"><strong>Why can a public page still return 403?</strong><p>A CDN may require JavaScript, cookies, or a browser challenge. The retriever reports that refusal instead of bypassing it.</p></aside>
        </section>

        <footer className="scrunch-explain-cta">
          <div className="scrunch-explain-cta-heading"><div><p>READY TO SEE IT WORK?</p><h2>Try the live retriever.</h2></div><Link to="/scrunch-demo">Open interactive demo →</Link></div>
          <div className="scrunch-try-grid">
            <section>
              <h3><span>✓</span> Good live requests</h3>
              <ul>
                <li><code>https://example.com</code><small>Small, dependable HTML page</small></li>
                <li><code>https://en.wikipedia.org/wiki/Web_scraping</code><small>Rich article; great with AI extraction</small></li>
                <li><code>https://httpbin.org/redirect/1</code><small>Shows redirect handling</small></li>
              </ul>
            </section>
            <section>
              <h3><span>!</span> Useful failure cases</h3>
              <ul>
                <li><code>http://127.0.0.1</code><small>Unsafe destination · blocked by SSRF protection</small></li>
                <li><code>https://www.webpagetest.org</code><small>Access refused · commonly returns 403</small></li>
                <li><code>https://demo.scrunch.invalid/not-found</code><small>Missing page · simulated HTTP 404</small></li>
                <li><code>https://demo.scrunch.invalid/private</code><small>Disallowed · simulated robots.txt denial</small></li>
                <li><code>https://demo.scrunch.invalid/offline</code><small>Network failure · simulated unreachable host</small></li>
                <li><code>https://demo.scrunch.invalid/slow</code><small>Timeout · simulated slow destination</small></li>
                <li><code>https://demo.scrunch.invalid/too-large</code><small>Size limit · simulated HTTP 413</small></li>
                <li><code>https://demo.scrunch.invalid/rate-limited</code><small>Rate limit · simulated HTTP 429</small></li>
              </ul>
            </section>
          </div>
          <p className="scrunch-example-note">Third-party responses can change. The <code>demo.scrunch.invalid</code> options in the retriever are deterministic simulations for repeatable testing.</p>
        </footer>
      </article>
    </main>
  );
}
